import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "../utils/api";
import type { PdsProjectDetails as PdsProjectDetailsType } from "types/pds";

interface PdsProjectDetailsProps {
  pdsProjectId: string;
  projectId: string;
  onUnlinked?: () => void;
}

const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

const PdsProjectDetails = ({
  pdsProjectId,
  projectId,
  onUnlinked,
}: PdsProjectDetailsProps) => {
  const { t } = useTranslation();
  const [details, setDetails] = useState<PdsProjectDetailsType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  useEffect(() => {
    if (!pdsProjectId) return;

    const fetchDetails = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await api.fetchPdsProjectByUuid(pdsProjectId);
        setDetails(data);
      } catch (err) {
        console.error("Error fetching PDS project details:", err);
        setError(
          t(
            "failed_to_load_pds_details",
            "Failed to load PDS project details",
          ),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [pdsProjectId, t]);

  const handleUnlink = async (
    e: React.MouseEvent | React.KeyboardEvent,
  ) => {
    e.stopPropagation();

    if (
      !window.confirm(
        t(
          "confirm_unlink_pds",
          "Are you sure you want to unlink this PDS project?",
        ),
      )
    ) {
      return;
    }

    setIsUnlinking(true);
    try {
      await api.unlinkPdsProject(projectId);
      toast.success(
        t("pds_project_unlinked", "PDS project unlinked successfully"),
      );
      if (onUnlinked) {
        onUnlinked();
      }
    } catch (err) {
      console.error("Error unlinking PDS project:", err);
      toast.error(
        t("failed_to_unlink_pds_project", "Failed to unlink PDS project"),
      );
    } finally {
      setIsUnlinking(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
          <span className="text-sm text-gray-500">
            {t("loading_pds_details", "Loading PDS project details...")}
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (!details) return null;

  const anschrift = details.anschrift;
  const status = details.vorgangStatus;
  const selektionen = details.selektionskriterien || [];
  const infoFelder = (details.projektInfoFelder || []).filter(
    (f) => f.wert && f.wert.trim() !== "",
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden">
      {/* Accordion Header - always visible */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <img src="/pds-logo.png" alt="PDS" className="h-6 w-auto" />
          <h3 className="text-lg font-semibold text-gray-900">
            {t("pds_project_details", "PDS Project Details")}
          </h3>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {t("linked", "Linked")}
          </span>
          {!isOpen && details.nummer && (
            <span className="text-sm text-gray-500">
              — {details.nummer}{" "}
              {details.bezeichnung ? `· ${details.bezeichnung}` : ""}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span
            role="button"
            tabIndex={0}
            onClick={handleUnlink}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleUnlink(e);
            }}
            className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              isUnlinking
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-red-50 text-red-600 hover:bg-red-100"
            }`}
          >
            {isUnlinking
              ? t("unlinking", "Unlinking...")
              : t("unlink", "Unlink")}
          </span>
          <ChevronIcon isOpen={isOpen} />
        </div>
      </button>

      {/* Accordion Content */}
      {isOpen && (
        <div className="px-6 pb-6 border-t border-gray-100">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 mb-6">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                {t("pds_project_number", "Project Number")}
              </label>
              <p className="text-sm text-gray-900 font-medium">
                {details.nummer || "—"}
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                {t("pds_designation", "Designation")}
              </label>
              <p className="text-sm text-gray-900">
                {details.bezeichnung || "—"}
              </p>
            </div>

            {details.externeNummer && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  {t("pds_external_number", "External Number")}
                </label>
                <p className="text-sm text-gray-900">
                  {details.externeNummer}
                </p>
              </div>
            )}

            {status && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  {t("pds_status", "Status")}
                </label>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    status.kategorie === "Offen"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {status.bezeichnung}
                </span>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                DB ID
              </label>
              <p className="text-sm text-gray-900">{details.dbID}</p>
            </div>

            {anschrift && (
              <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  {t("pds_anschrift", "Anschrift")}
                </label>
                <p className="text-sm text-gray-900">
                  {[
                    anschrift.strasse,
                    anschrift.zusatz,
                    `${anschrift.plz} ${anschrift.ort}`,
                    anschrift.land,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              </div>
            )}
          </div>

          {/* Project Info Fields (only non-empty) */}
          {infoFelder.length > 0 && (
            <div className="border-t border-gray-100 pt-4 mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                {t("pds_project_info", "Project Information")}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {infoFelder.map((item, idx) => (
                  <div key={idx}>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {item.bezeichnung}
                    </label>
                    <p className="text-sm text-gray-900">{item.wert}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selection Criteria */}
          {selektionen.length > 0 && (
            <div className="border-t border-gray-100 pt-4 mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                {t("pds_selection_criteria", "Selection Criteria")}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {selektionen.map((item, idx) => (
                  <div key={idx}>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {item.bezeichnung}
                    </label>
                    <p className="text-sm text-gray-900">
                      {item.wert || "—"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default PdsProjectDetails;
